import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    FaStopwatch, FaTimes, FaCheck, FaCoffee,
    FaPowerOff, FaHistory, FaExchangeAlt, FaRoute,
    FaArrowRight, FaUser, FaShieldAlt, FaCircle,
    FaBolt, FaSatelliteDish, FaSyncAlt
} from 'react-icons/fa';
import api from '../services/api';
import './ModalCentralOperacoes.css';

// ─── HELPERS ──────────────────────────────────────────────────────
const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
};

const formatTimeDuration = (minutes) => {
    const total = parseInt(minutes, 10) || 0;
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${h.toString().padStart(2, '0')}h ${m.toString().padStart(2, '0')}m`;
};

const STATUS_CONFIG = {
    livre: {
        label: 'LIVRE',
        color: '#10b981',
        glow: 'rgba(16,185,129,0.22)',
        soft: 'rgba(16,185,129,0.08)',
        icon: <FaCheck />
    },
    pausado: {
        label: 'EM PAUSA',
        color: '#f59e0b',
        glow: 'rgba(245,158,11,0.22)',
        soft: 'rgba(245,158,11,0.08)',
        icon: <FaCoffee />
    },
    em_deslocamento: {
        label: 'EM ROTA',
        color: '#3b82f6',
        glow: 'rgba(59,130,246,0.22)',
        soft: 'rgba(59,130,246,0.08)',
        icon: <FaRoute />
    },
    em_atendimento: {
        label: 'OCUPADO',
        color: '#ef4444',
        glow: 'rgba(239,68,68,0.22)',
        soft: 'rgba(239,68,68,0.08)',
        icon: <FaBolt />
    },
    offline: {
        label: 'OFFLINE',
        color: '#64748b',
        glow: 'rgba(100,116,139,0.22)',
        soft: 'rgba(100,116,139,0.08)',
        icon: <FaPowerOff />
    },
};

// ─── CONFIRM DIALOG INLINE ───────────────────────────────────────
function ConfirmDialog({ mensagem, onConfirm, onCancel, statusColor }) {
    return (
        <div className="mco-confirm-overlay">
            <div className="mco-confirm-box">
                <div
                    className="mco-confirm-icon"
                    style={{
                        color: statusColor || '#3b82f6',
                        borderColor: `${statusColor || '#3b82f6'}33`,
                        background: `${statusColor || '#3b82f6'}14`
                    }}
                >
                    <FaShieldAlt />
                </div>

                <div className="mco-confirm-eyebrow">CONFIRMAÇÃO DE AÇÃO</div>

                <p className="mco-confirm-msg">{mensagem}</p>

                <div className="mco-confirm-actions">
                    <button className="mco-btn-cancel" onClick={onCancel}>
                        Cancelar
                    </button>
                    <button className="mco-btn-confirm" onClick={onConfirm}>
                        Confirmar alteração
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── TOAST ───────────────────────────────────────────────────────
function Toast({ msg, tipo }) {
    return (
        <div className={`mco-toast mco-toast-${tipo}`}>
            <div className="mco-toast-icon">
                {tipo === 'ok' ? <FaCheck /> : <FaTimes />}
            </div>
            <span>{msg}</span>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════
export default function ModalCentralOperacoes({ isOpen, onClose, driver, onUpdateDriver }) {
    const [logs, setLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [loadingAcao, setLoadingAcao] = useState(false);
    const [motivo, setMotivo] = useState('');
    const [confirm, setConfirm] = useState(null); // { status, label, color }
    const [toast, setToast] = useState(null);     // { msg, tipo }
    const toastTimer = useRef(null);

    const fetchLogs = useCallback(async () => {
        if (!driver?.id) return;
        setLoadingLogs(true);
        try {
            const token = localStorage.getItem('token');
            const { data } = await api.get(`/api/operations/admin/logs/${driver.id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setLogs(data || []);
        } catch (err) {
            console.error('Erro ao buscar logs:', err);
        } finally {
            setLoadingLogs(false);
        }
    }, [driver?.id]);

    useEffect(() => {
        if (isOpen && driver?.id) {
            fetchLogs();
            setMotivo('');
            setConfirm(null);
            setToast(null);
        }
    }, [isOpen, driver?.id, fetchLogs]);

    const showToast = useCallback((msg, tipo = 'ok') => {
        setToast({ msg, tipo });
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), 3200);
    }, []);

    useEffect(() => {
        return () => clearTimeout(toastTimer.current);
    }, []);

    const executarMudancaStatus = async (novoStatus) => {
        setConfirm(null);
        setLoadingAcao(true);

        try {
            const token = localStorage.getItem('token');

            await api.put(
                `/api/operations/admin/status/${driver.id}`,
                {
                    status: novoStatus,
                    motivo: motivo || 'Alteração manual pelo administrador'
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            onUpdateDriver(driver.id, { status_operacional: novoStatus });
            await fetchLogs();
            setMotivo('');
            showToast('Status atualizado com sucesso.', 'ok');
        } catch (err) {
            console.error('Erro na requisição:', err);
            showToast('Falha ao atualizar status.', 'erro');
        } finally {
            setLoadingAcao(false);
        }
    };

    const pedirConfirmacao = (novoStatus) => {
        const cfg = STATUS_CONFIG[novoStatus];
        setConfirm({
            status: novoStatus,
            label: cfg?.label || novoStatus,
            color: cfg?.color || '#3b82f6'
        });
    };

    if (!isOpen || !driver) return null;

    const statusAtual = STATUS_CONFIG[driver.status_operacional] || STATUS_CONFIG.offline;

    const ACOES = [
        { status: 'livre', icon: <FaCheck />, label: 'LIVRE' },
        { status: 'pausado', icon: <FaCoffee />, label: 'PAUSA' },
        { status: 'em_deslocamento', icon: <FaRoute />, label: 'ROTA' },
        { status: 'offline', icon: <FaPowerOff />, label: 'OFFLINE' },
    ];

    return (
        <div className="mco-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="mco-modal">

                {/* HEADER */}
                <header className="mco-header">
                    <div className="mco-header-left">
                        <div
                            className="mco-avatar"
                            style={{
                                borderColor: `${statusAtual.color}33`,
                                boxShadow: `0 0 0 4px ${statusAtual.glow}`
                            }}
                        >
                            <FaUser />
                            <span
                                className="mco-avatar-dot"
                                style={{
                                    background: statusAtual.color,
                                    boxShadow: `0 0 0 4px ${statusAtual.glow}`
                                }}
                            />
                        </div>

                        <div className="mco-header-info">
                            <div className="mco-header-topline">
                                <span className="mco-header-eyebrow">CONTROL CENTER</span>
                                <span className="mco-header-divider" />
                                <span className="mco-id-chip">ID #{driver.id}</span>
                            </div>

                            <h2 className="mco-driver-name">{driver.nome}</h2>

                            <div className="mco-driver-meta">
                                <span
                                    className="mco-status-chip"
                                    style={{
                                        color: statusAtual.color,
                                        background: statusAtual.soft,
                                        borderColor: `${statusAtual.color}33`,
                                        boxShadow: `0 0 0 1px ${statusAtual.glow}`
                                    }}
                                >
                                    <FaCircle style={{ fontSize: '0.45rem' }} />
                                    {statusAtual.label}
                                </span>

                                <span className="mco-signal-chip">
                                    <FaSatelliteDish />
                                    MONITORAMENTO ATIVO
                                </span>
                            </div>
                        </div>
                    </div>

                    <button className="mco-close" onClick={onClose} title="Fechar">
                        <FaTimes />
                    </button>
                </header>

                {/* CORPO */}
                <div className="mco-body">

                    {/* COLUNA ESQUERDA */}
                    <aside className="mco-side">
                        <div className="mco-side-panel">
                            <div className="mco-section-title">
                                <span>Indicadores Operacionais</span>
                            </div>

                            <div className="mco-kpi-grid">
                                <div className="mco-kpi-card">
                                    <div className="mco-kpi-icon mco-kpi-icon--amber">
                                        <FaStopwatch />
                                    </div>
                                    <div>
                                        <span className="mco-kpi-label">Pausa no dia</span>
                                        <span className="mco-kpi-value">{formatTimeDuration(driver.tempo_pausa_hoje)}</span>
                                    </div>
                                </div>

                                <div className="mco-kpi-card">
                                    <div
                                        className="mco-kpi-icon"
                                        style={{
                                            background: statusAtual.soft,
                                            color: statusAtual.color,
                                            boxShadow: `inset 0 0 0 1px ${statusAtual.glow}`
                                        }}
                                    >
                                        <FaExchangeAlt />
                                    </div>
                                    <div>
                                        <span className="mco-kpi-label">Status atual</span>
                                        <span className="mco-kpi-value" style={{ color: statusAtual.color }}>
                                            {statusAtual.label}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mco-side-panel">
                            <div className="mco-divider">
                                <span>INTERVENÇÃO ADMINISTRATIVA</span>
                            </div>

                            <div className="mco-input-wrap">
                                <label className="mco-input-label">Motivo da alteração</label>
                                <input
                                    className="mco-input"
                                    type="text"
                                    placeholder="Ex: fim de expediente, deslocamento externo, indisponibilidade..."
                                    value={motivo}
                                    onChange={e => setMotivo(e.target.value)}
                                    disabled={loadingAcao}
                                />
                            </div>

                            <div className="mco-actions-grid">
                                {ACOES.map((a) => {
                                    const cfg = STATUS_CONFIG[a.status];
                                    const isAtivo = driver.status_operacional === a.status;

                                    return (
                                        <button
                                            key={a.status}
                                            className={`mco-action-btn ${isAtivo ? 'mco-action-btn--ativo' : ''}`}
                                            style={{
                                                '--btn-color': cfg.color,
                                                '--btn-glow': cfg.glow,
                                                '--btn-soft': cfg.soft
                                            }}
                                            onClick={() => pedirConfirmacao(a.status)}
                                            disabled={loadingAcao || isAtivo}
                                            title={isAtivo ? 'Status atual' : `Mudar para ${a.label}`}
                                        >
                                            <span className="mco-action-icon">{a.icon}</span>
                                            <span className="mco-action-label">{a.label}</span>
                                            {isAtivo && <span className="mco-action-badge">ATUAL</span>}
                                        </button>
                                    );
                                })}
                            </div>

                            {loadingAcao && (
                                <div className="mco-processing">
                                    <div className="mco-spinner" />
                                    <span>Processando alteração...</span>
                                </div>
                            )}
                        </div>
                    </aside>

                    {/* COLUNA DIREITA */}
                    <main className="mco-main">
                        <div className="mco-main-header">
                            <div className="mco-history-title">
                                <FaHistory />
                                <span>Timeline de atividades</span>
                            </div>

                            <button
                                className="mco-refresh-btn"
                                onClick={fetchLogs}
                                disabled={loadingLogs}
                                title="Atualizar"
                            >
                                {loadingLogs ? <div className="mco-spinner mco-spinner--sm" /> : <FaSyncAlt />}
                            </button>
                        </div>

                        <div className="mco-timeline">
                            {loadingLogs && logs.length === 0 ? (
                                Array.from({ length: 4 }).map((_, i) => (
                                    <div key={i} className="mco-log-skeleton" style={{ animationDelay: `${i * 0.08}s` }} />
                                ))
                            ) : logs.length === 0 ? (
                                <div className="mco-empty">
                                    <div className="mco-empty-icon">
                                        <FaHistory />
                                    </div>
                                    <div className="mco-empty-title">Sem histórico recente</div>
                                    <p>Nenhuma alteração administrativa foi registrada até o momento.</p>
                                </div>
                            ) : (
                                logs.map((log) => {
                                    const cfgAnterior = STATUS_CONFIG[log.status_anterior] || STATUS_CONFIG.offline;
                                    const cfgNovo = STATUS_CONFIG[log.status_novo] || STATUS_CONFIG.offline;

                                    return (
                                        <div
                                            key={`${log.data_alteracao}-${log.status_novo}-${log.status_anterior}`}
                                            className="mco-log-item"
                                        >
                                            <div
                                                className="mco-log-dot"
                                                style={{
                                                    background: cfgNovo.color,
                                                    boxShadow: `0 0 0 4px ${cfgNovo.glow}`
                                                }}
                                            />

                                            <div className="mco-log-card">
                                                <div className="mco-log-card-top">
                                                    <div className="mco-log-transition">
                                                        <span
                                                            className="mco-log-tag"
                                                            style={{
                                                                color: cfgAnterior.color,
                                                                background: cfgAnterior.soft,
                                                                borderColor: `${cfgAnterior.color}33`
                                                            }}
                                                        >
                                                            {cfgAnterior.label}
                                                        </span>

                                                        <FaArrowRight className="mco-log-arrow" />

                                                        <span
                                                            className="mco-log-tag"
                                                            style={{
                                                                color: cfgNovo.color,
                                                                background: cfgNovo.soft,
                                                                borderColor: `${cfgNovo.color}33`
                                                            }}
                                                        >
                                                            {cfgNovo.label}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="mco-log-footer">
                                                    <span className="mco-log-by">
                                                        {log.alterado_por || 'Sistema'}
                                                    </span>
                                                    <span className="mco-log-time">
                                                        {formatDateTime(log.data_alteracao)}
                                                    </span>
                                                </div>

                                                {log.motivo && (
                                                    <div className="mco-log-reason">
                                                        “{log.motivo}”
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </main>
                </div>

                {/* CONFIRM DIALOG */}
                {confirm && (
                    <ConfirmDialog
                        mensagem={`Confirmar alteração forçada para ${confirm.label}?`}
                        statusColor={confirm.color}
                        onConfirm={() => executarMudancaStatus(confirm.status)}
                        onCancel={() => setConfirm(null)}
                    />
                )}

                {/* TOAST */}
                {toast && <Toast msg={toast.msg} tipo={toast.tipo} />}
            </div>
        </div>
    );
}